import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Edit, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { DataTable, type Column } from "@/components/portal/DataTable"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"

type Tenant = NonNullable<Awaited<ReturnType<typeof fetchTenants>>>["items"][number]

async function fetchTenants() {
  const { data, error } = await api.GET("/api/v1/tenants", {
    params: { query: { include_test: true } },
  })
  if (error) throw new Error(String(error))
  return data
}

export default function TenantsList() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["tenants-list"],
    queryFn: fetchTenants,
  })

  const del = useMutation({
    mutationFn: async (slug: string) => {
      const r = await fetch(`/api/v1/tenants/${slug}`, { method: "DELETE", credentials: "include" })
      if (!r.ok && r.status !== 302) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => {
      toast("✓ Tenant geloescht")
      qc.invalidateQueries({ queryKey: ["tenants-list"] })
    },
    onError: (e) => toast("Loeschen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  const columns: Column<Tenant>[] = [
    {
      key: "name",
      header: "Name",
      render: (t) => (
        <div>
          <div className="font-medium">{t.name}</div>
          <div className="text-[11px] text-muted-foreground">{t.slug}</div>
        </div>
      ),
    },
    {
      key: "kunde",
      header: "Kunde",
      render: (t) => <span className="text-[13px]">{t.kunden_slug || "—"}</span>,
    },
    {
      key: "imap",
      header: "IMAP-User",
      render: (t) => <span className="text-[12px] text-muted-foreground">{t.imap_user || "—"}</span>,
    },
    {
      key: "active",
      header: "Aktiv",
      width: "80px",
      render: (t) => (
        <span className={t.active ? "text-success text-xs font-semibold" : "text-muted-foreground text-xs"}>
          {t.active ? "aktiv" : "inaktiv"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "180px",
      render: (t) => (
        <div className="flex items-center gap-3">
          <Link
            to={`/tenants/${t.slug}`}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-primary hover:underline whitespace-nowrap"
          >
            <Edit className="w-3 h-3" /> Bearbeiten
          </Link>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Tenant "${t.name}" (${t.slug}) wirklich loeschen?\n\nAlle zugehoerigen Daten (E-Mails, Anrufe, Tasks) bleiben in der DB, der Tenant wird nur deaktiviert.`)) {
                del.mutate(t.slug)
              }
            }}
            disabled={del.isPending}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-destructive hover:underline disabled:opacity-50 whitespace-nowrap"
          >
            <Trash2 className="w-3 h-3" /> Loeschen
          </button>
        </div>
      ),
    },
  ]

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  return (
    <div className="px-10 py-8 max-w-[1440px]">
      <PageHeader title="Tenants" count={data?.total} onRefresh={() => refetch()} refreshing={isFetching}>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-3.5 h-3.5" /> Neuer Tenant
        </button>
      </PageHeader>
      {isLoading ? (
        <Loading label="Lädt Tenants..." />
      ) : (
        <DataTable
          rows={data?.items ?? []}
          columns={columns}
          rowKey={(t) => t.slug}
          emptyText="Keine Tenants gefunden"
        />
      )}

      <CreateTenantModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => qc.invalidateQueries({ queryKey: ["tenants-list"] })} />
    </div>
  )
}

function CreateTenantModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [kundenSlug, setKundenSlug] = useState("")
  const [email, setEmail] = useState("")
  const [heroToken, setHeroToken] = useState("")
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const submit = async () => {
    if (!name.trim() || !slug.trim()) {
      toast("Name und Slug sind Pflicht", "error")
      return
    }
    setSubmitting(true)
    try {
      const form = new URLSearchParams()
      form.append("name", name)
      form.append("slug", slug.toLowerCase().replace(/\s+/g, "_"))
      form.append("kunden_slug", kundenSlug)
      form.append("email", email)
      form.append("hero_token", heroToken)
      form.append("imap_port", "993")
      form.append("imap_folder", "INBOX")
      const r = await fetch("/admin/tenant/new", { method: "POST", credentials: "include", body: form })
      if (!r.ok && r.status !== 302 && r.status !== 303) {
        throw new Error(`HTTP ${r.status}`)
      }
      toast("✓ Tenant angelegt")
      setName(""); setSlug(""); setKundenSlug(""); setEmail(""); setHeroToken("")
      onCreated()
      onClose()
    } catch (e) {
      toast("Anlegen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="pp-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          background: "var(--bg-card, white)", borderRadius: 12, padding: 24,
          width: 520, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-[17px] font-semibold mb-4">Neuer Tenant</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="pp-input w-full" placeholder="z.B. Musterfirma GmbH" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Slug * (lowercase, keine Leerzeichen)</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} className="pp-input w-full" placeholder="z.B. musterfirma" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Kunden-Slug</label>
            <input value={kundenSlug} onChange={(e) => setKundenSlug(e.target.value)} className="pp-input w-full" placeholder="Optional — uebergeordneter Kunde" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">E-Mail (Outgoing-Absender)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pp-input w-full" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">HERO Token</label>
            <input value={heroToken} onChange={(e) => setHeroToken(e.target.value)} className="pp-input w-full" placeholder="Optional — kann nach Create in Tenant-Edit gesetzt werden" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Weitere Felder (IMAP, Smart Inbox) in der Tenant-Edit-Seite setzen.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={onClose} className="pp-btn pp-btn-sm" disabled={submitting}>Abbrechen</button>
            <button
              onClick={submit}
              disabled={submitting || !name.trim() || !slug.trim()}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "Erstelle..." : "Tenant anlegen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
